<template>
  <div class="px-6 py-5 overflow-y-auto right-content-grid-vs h-[calc(100vh-309px)]">
    <div class="text-[#414141] font-sans">
      <h3 class="text-[1.25rem] leading-[1.75rem] mb-[1rem] font-semibold">
        Overview
      </h3>

      <p class="text-[14px] leading-[22px] mb-[0.5rem]">This module allows your organization to enter results for each Key Performance Indicator (KPI). Reporting KPI results helps funders and donors understand your programs’ progress, strengthening transparency, trust, and credibility. Even when a KPI has not been achieved, clearly communicating current results, contributing factors, and next steps demonstrates accountability and a commitment to continuous improvement.</p>

      <p class="text-[14px] leading-[22px] mb-[0.5rem]">The KPIs established for each Core Annual Program in Module 4 will automatically appear in this section. Simply enter the corresponding results.</p>
    </div>
  </div>

  <div v-if="showUnlinkedOutcomePopup" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <h3 class="text-lg font-semibold mb-4 text-[#00423D]">Link outcomes before continuing</h3>
      <p class="text-gray-600 mb-6">
        One or more outcome theme(s) are not connected to a core annual program yet. Please go back to
        <span class="font-semibold">Naming Your Core Annual Programs</span>
        section to assign each outcome to a program, or create a new core program if needed.
      </p>
      <div class="flex justify-end gap-4">
        <button
          @click="onCancelPopup"
          class="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          @click="onGoToCorePrograms"
          class="px-4 py-2 bg-[#00423D] text-white rounded-lg hover:bg-[#003632]"
        >
          Go to Module 4
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useStepValidation } from '@/composables/useStepValidation';
import { useAuthStore } from '@/stores/auth';
import global from '@/global';

const router = useRouter();
const authStore = useAuthStore();

const showUnlinkedOutcomePopup = ref(false);
const hasUnlinkedOutcomes = ref(false);

const updateValidationState = () => {
  showUnlinkedOutcomePopup.value = hasUnlinkedOutcomes.value;
  return !hasUnlinkedOutcomes.value;
};

const fetchOutcomeLinkStatus = async () => {
  if (!authStore.user?.accessToken) {
    hasUnlinkedOutcomes.value = false;
    return;
  }

  try {
    const response = await fetch(`${global.API_URL}/outcomes/link-status`, {
      headers: {
        Authorization: `Bearer ${authStore.user.accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const data = await response.json();
    hasUnlinkedOutcomes.value = Boolean(data?.has_unlinked_outcomes);
  } catch (error) {
    console.error('Failed to fetch outcome link status:', error);
    hasUnlinkedOutcomes.value = false;
  }
};

const onCancelPopup = () => {
  showUnlinkedOutcomePopup.value = false;
};

const onGoToCorePrograms = () => {
  showUnlinkedOutcomePopup.value = false;
  router.push('/steps/funding-annual/core-programs');
};

useStepValidation({
  validateFn: async () => updateValidationState(),
});

onMounted(async () => {
  await fetchOutcomeLinkStatus();
  updateValidationState();
});
</script>

<style scoped>
</style>
